import { AsyncPipe, CurrencyPipe, DatePipe, UpperCasePipe } from '@angular/common';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { SavingsPlanFacade } from '@zero/saving-plans/domain';
import { ContractConfirmationFacade } from '@zero/shared/feature-contract-confirmation/base';
import { MicarContractsNudgingComponent } from '@zero/shared/feature-contract-confirmation/web';
import { IncludesPipe } from '@zero/shared/ui-pipes';
import { WatchlistToggleComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { InstrumentType } from '@zero/shared/util-models';
import { CheckExperienceAndFtgComponent } from '@zero/trade/feature-trade/web';
import { commonRoutes } from 'constants/web-app-constants';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { IgnoreThousandSeparatorPipe } from '../../pipes/ignore-thousand-separator.pipe';
import { DonauCapitalHintComponent } from '../donau-capital-hint/donau-capital-hint.component';
import { SavingsPlanDynamizationComponent } from '../savings-plan-dynamization/savings-plan-dynamization.component';

@Component({
  selector: 'saving-plans-edit',
  templateUrl: './savings-plan-edit.component.html',
  imports: [
    TranslocoPipe,
    AsyncPipe,
    DatePipe,
    WatchlistToggleComponent,
    CurrencyPipe,
    IncludesPipe,
    ButtonComponent,
    UpperCasePipe,
    CheckExperienceAndFtgComponent,
    SavingsPlanDynamizationComponent,
    RouterLink,
    IgnoreThousandSeparatorPipe,
    DonauCapitalHintComponent,
    MicarContractsNudgingComponent,
  ],
})
export class SavingsPlanEditComponent {
  @Output() public noWalletWarningClick = new EventEmitter();
  @Output() public goNext = new EventEmitter();
  @Output() public resetAsset = new EventEmitter();

  public readonly savingPlansRoute = commonRoutes.postlogin.savings_plans;
  public readonly savingsPlanFacade = inject(SavingsPlanFacade);
  public readonly customerStateFacade = inject(CustomerStateFacade);
  public readonly contractConfirmationFacade = inject(ContractConfirmationFacade);
  public readonly InstrumentType = InstrumentType;

  readonly #acceptCryptoRisk$ = new BehaviorSubject<boolean>(false);

  public readonly isFormValid$ = combineLatest([
    this.savingsPlanFacade.existingSavingsPlanError$,
    this.customerStateFacade.customerBaseData$,
    this.savingsPlanFacade.selectedAsset$,
    this.contractConfirmationFacade.shouldDisplayNudgingNotification$,
    this.#acceptCryptoRisk$,
  ]).pipe(
    map(([error, customerBaseData, selectedAsset, shouldDisplayNudgingNotification, acceptCryptoRisk]) => {
      if (error) {
        return false;
      }

      if (selectedAsset?.instrumentType === InstrumentType.Crypto) {
        if (!acceptCryptoRisk || !customerBaseData?.hasActiveWallet || shouldDisplayNudgingNotification) {
          return false;
        }
      }

      return true;
    }),
  );

  public acceptRisk(accept: boolean): void {
    this.#acceptCryptoRisk$.next(accept);
  }

  public changeAmount = (ev: Event) => {
    const value = ev.target['value'];
    const amount = value ? Number(value.replace(',', '.')) : null;
    this.savingsPlanFacade.updateExistingSavingsPlanAmount(amount);
  };
}
